/* eslint-disable max-lines */
import { Injectable } from '@angular/core';
import { BookWidgetAem } from '@com-delta-idp/mnx-capability-flight-shop-ui';
import { AppConfigService } from '@com-delta-idp/mnx-utils';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Action, Store } from '@ngrx/store';
import { Observable, of } from 'rxjs';
import { filter, switchMap } from 'rxjs/operators';
import { prepareBookWidgetFields } from '../../../adapters/book-widget-fields-init/book-widget-fields-init.adapter';
import { FlightShopWidgetTemplates } from '../../../constants/flight-shop.constants';
import { EndpointKeys, IDP_APPS } from '../../../constants/urls.constants';
import { BookWidgetFieldsMasterConfig } from '../../../models/book-widget-fields-master-config.interfaces';
import { CatalogApiFieldsValue } from '../../../models/core-apis/catalog-api.interfaces';
import { DefaultLookupFieldsValue } from '../../../models/core-apis/default-lookup.interfaces';
import { GeoLocationResponse } from '../../../models/core-apis/geo-location.interfaces';
import { RetrieveSearchData } from '../../../models/core-apis/retrieve-search.interfaces';
import { LoginDashboardFieldsConfig } from '../../../models/login-dashboard.interfaces';
import { QueryParams } from '../../../models/states/query-params-state.interfaces';
import * as fromAemActions from '../../actions/aem.actions';
import * as fromBookWidgetActions from '../../actions/book-widget.actions';
import * as fromCatalogApiActions from '../../actions/catalog-api.actions';
import * as fromDefaultLookupActions from '../../actions/default-lookup.actions';
import * as fromGeoLocationActions from '../../actions/geo-location.actions';
import * as fromLoginDashboardActions from '../../actions/login-dashboard.actions';
import * as fromRetrieveSearchActions from '../../actions/retrieve-search.actions';
import { getBookWidgetAem } from '../../selectors/aem.selectors';
import {
  getBookWidgetFieldsMasterConfig,
  getFlightShopTemplateType,
  isBookWidgetReady
} from '../../selectors/book-widget.selectors';
import { getCatalogApiFieldsValue } from '../../selectors/catalog-api.selectors';
import { getDefaultsLookupFieldsValue } from '../../selectors/default-lookup.selectors';
import { getGeoLocationData } from '../../selectors/geo-location.selectors';
import {
  getLoginDashboardFieldsConfig,
  isBusinessUser
} from '../../selectors/login-dashboard.selectors';
import {
  getHasDeepLinkingParams,
  getQueryParams
} from '../../selectors/query-params.selectors';
import { getRetrieveSearchData } from '../../selectors/retrieve-search.selectors';

@Injectable({
  providedIn: 'root'
})
export class BookWidgetFieldsInitEffects {
  defaultValueEvaluation$ = createEffect(
    (): Observable<Action> =>
      this.Actions$.pipe(
        ofType(
          fromBookWidgetActions.TriggerInitFields,
          fromAemActions.LoadBookWidgetAemSuccess,
          fromAemActions.LoadBookWidgetAemFailure,
          fromCatalogApiActions.LoadCatalogApiSuccess,
          fromCatalogApiActions.LoadCatalogApiFailure,
          fromDefaultLookupActions.LoadDefaultLookupLandingSuccess,
          fromDefaultLookupActions.LoadDefaultLookupLandingFailure,
          fromGeoLocationActions.LoadGeoLocationSuccess,
          fromGeoLocationActions.LoadGeoLocationFailure,
          fromRetrieveSearchActions.LoadRetrieveSearchSuccess,
          fromRetrieveSearchActions.LoadRetrieveSearchFailure
        ),
        concatLatestFrom(() => this.store.select(isBookWidgetReady)),
        filter(([, isBookWidgetReady]) => isBookWidgetReady),
        concatLatestFrom(() => [
          this.store.select(getBookWidgetAem),
          this.store.select(getCatalogApiFieldsValue),
          this.store.select(getDefaultsLookupFieldsValue),
          this.store.select(getGeoLocationData),
          this.store.select(getRetrieveSearchData),
          this.store.select(getBookWidgetFieldsMasterConfig),
          this.store.select(getQueryParams),
          this.store.select(getLoginDashboardFieldsConfig),
          this.store.select(getHasDeepLinkingParams)
        ]),
        switchMap(this._initBookWidgetFields.bind(this))
      )
  );

  //As dashboard is always called, but we need to listen its success/failure only when its business user.
  //Hence moved the logic into separate effect
  defaultValueEvaluationBusiness$ = createEffect(
    (): Observable<Action> =>
      this.Actions$.pipe(
        ofType(
          fromLoginDashboardActions.LoadDashboardLandingSuccess,
          fromLoginDashboardActions.LoadDashboardLandingFailure
        ),
        concatLatestFrom(() => [
          this.store.select(isBookWidgetReady),
          this.store.select(isBusinessUser)
        ]),
        filter(
          ([, isBookWidgetReady, isBusinessUserValue]) =>
            isBookWidgetReady && isBusinessUserValue
        ),
        concatLatestFrom(() => [
          this.store.select(getBookWidgetAem),
          this.store.select(getCatalogApiFieldsValue),
          this.store.select(getDefaultsLookupFieldsValue),
          this.store.select(getGeoLocationData),
          this.store.select(getRetrieveSearchData),
          this.store.select(getBookWidgetFieldsMasterConfig),
          this.store.select(getQueryParams),
          this.store.select(getLoginDashboardFieldsConfig),
          this.store.select(getHasDeepLinkingParams)
        ]),
        switchMap(this._initBookWidgetFields.bind(this))
      )
  );

  //Init booking widget from AEM data (only when template type is home)
  defaultValueEvaluationForHomepage$ = createEffect(
    (): Observable<Action> =>
      this.Actions$.pipe(
        ofType(fromAemActions.LoadBookWidgetAemSuccess),
        concatLatestFrom(() => [this.store.select(getFlightShopTemplateType)]),
        filter(
          ([, flightShopTemplateType]) =>
            flightShopTemplateType === FlightShopWidgetTemplates.HOME
        ),
        concatLatestFrom(() => [
          this.store.select(getBookWidgetAem),
          this.store.select(getCatalogApiFieldsValue),
          this.store.select(getDefaultsLookupFieldsValue),
          this.store.select(getGeoLocationData),
          this.store.select(getRetrieveSearchData),
          this.store.select(getBookWidgetFieldsMasterConfig),
          this.store.select(getQueryParams),
          this.store.select(getLoginDashboardFieldsConfig),
          this.store.select(getHasDeepLinkingParams)
        ]),
        switchMap(this._initBookWidgetFields.bind(this))
      )
  );

  constructor(
    private Actions$: Actions,
    private store: Store,
    private appConfigService: AppConfigService
  ) {}

  private _initBookWidgetFields([
    ,
    bookWidgetAem,
    catalogApiFieldsValue,
    defaultLookupFieldsValue,
    geoLocationData,
    retrieveSearchData,
    bookWidgetFieldsMasterConfig,
    queryParams,
    loginDashboardFieldsConfig,
    hasDeepLinkingParams
  ]: [
    unknown,
    BookWidgetAem | null,
    CatalogApiFieldsValue | null,
    DefaultLookupFieldsValue | null,
    GeoLocationResponse | null,
    RetrieveSearchData | null,
    BookWidgetFieldsMasterConfig,
    QueryParams | null,
    LoginDashboardFieldsConfig | null,
    boolean
  ]) {
    const bookWidgetConfig = this.appConfigService.getConfig(
      IDP_APPS.BOOK_WIDGET
    );
    const edocsUrl = bookWidgetConfig[EndpointKeys.EDOCS_LINK_SRC];
    const bookWidgetFields = prepareBookWidgetFields(
      bookWidgetAem,
      catalogApiFieldsValue,
      defaultLookupFieldsValue,
      geoLocationData,
      retrieveSearchData,
      bookWidgetFieldsMasterConfig,
      queryParams,
      loginDashboardFieldsConfig,
      edocsUrl,
      hasDeepLinkingParams
    );
    return of(
      fromBookWidgetActions.InitBookWidgetFields({
        bookWidgetFields
      })
    );
  }
}
